/**
 * ExpenseSummaryBar — Vorsteuer-Cockpit (M8) presentation component.
 *
 * Renders the four cockpit key figures (net expenses, deductible input VAT,
 * owed VAT §13b, and the gross "Einnahmen − Ausgaben (brutto)" orientation
 * figure) plus a period selector (month / quarter / year). It owns ONLY the
 * selected-period state — all data arrives via props (single source: the same
 * `expenses` array the table already renders, plus an optional income array).
 *
 * NO Firebase import, NO own data loading (testability + exactly one query).
 * Aggregation is delegated to the pure `summarizeExpenses` util.
 *
 * A1 (PFLICHT): when `summary.excludedDraftVatOwedCount > 0`, a visible hint is
 * rendered under the "Geschuldete USt" tile so an unbooked §13b liability never
 * silently disappears (fail-open guard).
 *
 * A2 (❌-VETO): the gross-balance tile is titled "Einnahmen − Ausgaben (brutto)"
 * — the token "EÜR" appears in NO affirmative label/title; only the negating
 * disclaimer ("Keine … (EÜR)") may mention it. The disclaimer + basis note are
 * RENDERED (not tooltip-only) — since M-V3-D they live in the footer strip
 * below the tile grid (not inside the balance tile itself), so the four tiles
 * stay equal height. Position changed, wording did not (jurist-bound, §6.2 of
 * architecture-ux-density-v3.md).
 *
 * @module ExpenseSummaryBar
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconChartPie, IconWarning, IconSpinner } from '../../icons';

import { Expense } from '../../types/expenseTypes';
import { centsToEuros } from '../../utils/moneyUtils';
import {
  summarizeExpenses,
  resolvePeriod,
  SummaryPeriod,
  SummaryPeriodKind,
  IncomeEntry,
} from '../../utils/expenseSummary';

// ─── Props ──────────────────────────────────────────────────────────────────────

export interface ExpenseSummaryBarProps {
  /** Bereits geladene Ausgaben (dasselbe Array wie die Tabelle). */
  expenses: Expense[];
  /** Einnahmen für den Brutto-Saldo; null ⇒ Brutto-Saldo-Kachel ausgeblendet (fail-closed). */
  income: IncomeEntry[] | null;
  /** Lädt-Status der Einnahmen (zeigt Spinner nur in der Brutto-Saldo-Kachel). */
  incomeLoading?: boolean;
}

// ─── Helpers (pure, module-local) ───────────────────────────────────────────────

/** Zwei-stellige Null-gepaddete Zahl als String. */
function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Heutiges Datum als ISO YYYY-MM-DD (lokale Zeitzone). */
function getTodayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/**
 * Erzeugt auswählbare konkrete Perioden (heute + vorige), je nach Granularität.
 * Reine Funktion: Anker als Datums-Strings, dann `resolvePeriod`.
 */
function buildPeriodOptions(kind: SummaryPeriodKind, todayISO: string): SummaryPeriod[] {
  const year = Number.parseInt(todayISO.slice(0, 4), 10);
  const month = Number.parseInt(todayISO.slice(5, 7), 10);
  const anchors: string[] = [];

  if (kind === 'year') {
    for (let i = 0; i < 5; i++) anchors.push(`${year - i}-06-15`);
  } else if (kind === 'quarter') {
    const q = Math.ceil(month / 3);
    const absQ = year * 4 + (q - 1);
    for (let i = 0; i < 8; i++) {
      const a = absQ - i;
      const yy = Math.floor(a / 4);
      const qq = (a % 4) + 1;
      const midMonth = (qq - 1) * 3 + 2;
      anchors.push(`${yy}-${pad2(midMonth)}-15`);
    }
  } else {
    const absM = year * 12 + (month - 1);
    for (let i = 0; i < 12; i++) {
      const a = absM - i;
      const yy = Math.floor(a / 12);
      const mm = (a % 12) + 1;
      anchors.push(`${yy}-${pad2(mm)}-15`);
    }
  }

  return anchors.map((anchor) => resolvePeriod(kind, anchor));
}

// ─── Component ──────────────────────────────────────────────────────────────────

/**
 * The Vorsteuer-Cockpit summary bar shown above the expenses table.
 * Fail-closed: rendered by `ExpensesPage` only when the cockpit feature flag is on.
 */
const ExpenseSummaryBar: React.FC<ExpenseSummaryBarProps> = ({
  expenses,
  income,
  incomeLoading = false,
}) => {
  const { t, i18n } = useTranslation('expenses');
  const locale = i18n.language?.startsWith('de') ? 'de-DE' : i18n.language || 'de-DE';
  const todayISO = useMemo(() => getTodayISO(), []);

  // Default granularity = year: a quarter/month default often lands on an empty
  // current period while the user's expenses sit in an earlier period of the same
  // year → cockpit shows 0 € next to a non-empty table (looks broken). Year shows
  // the full-year picture immediately; quarter/month (UStVA cadence) is one click away.
  const [kind, setKind] = useState<SummaryPeriodKind>('year');
  const [period, setPeriod] = useState<SummaryPeriod>(() => resolvePeriod('year', todayISO));

  const periodOptions = useMemo(() => buildPeriodOptions(kind, todayISO), [kind, todayISO]);

  const summary = useMemo(
    () => summarizeExpenses(expenses, period, income),
    [expenses, period, income],
  );

  // ── Formatters ─────────────────────────────────────────────────────────────
  const currencyFmt = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
      }),
    [locale],
  );
  // Vorzeichen-aware Formatter für den Saldo (+/−).
  const signedCurrencyFmt = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        signDisplay: 'exceptZero',
      }),
    [locale],
  );

  const formatCents = (cents: number): string => currencyFmt.format(centsToEuros(cents));
  const formatSignedCents = (cents: number): string => signedCurrencyFmt.format(centsToEuros(cents));

  // ── Period label ───────────────────────────────────────────────────────────
  const periodLabel = (p: SummaryPeriod): string => {
    if (p.kind === 'year') return t('cockpit.period.yearLabel', { year: p.year });
    if (p.kind === 'quarter') {
      return t('cockpit.period.quarterLabel', { quarter: p.quarter, year: p.year });
    }
    // month — lokalisierter Monatsname
    const monthName = new Date(p.year, (p.month ?? 1) - 1, 1).toLocaleDateString(locale, {
      month: 'long',
      year: 'numeric',
    });
    return monthName;
  };

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleKindChange = (nextKind: SummaryPeriodKind) => {
    setKind(nextKind);
    // Beim Granularitäts-Wechsel auf die aktuelle Periode dieser Granularität springen.
    setPeriod(resolvePeriod(nextKind, todayISO));
  };

  const handlePeriodChange = (fromISO: string) => {
    const next = periodOptions.find((p) => p.fromISO === fromISO);
    if (next) setPeriod(next);
  };

  // Brutto-Saldo-Kachel nur zeigen, wenn Einnahmen verfügbar ODER gerade ladend (fail-closed).
  const showBalanceTile = income !== null || incomeLoading;
  const balanceCents = summary.euerBalanceCents;
  const balancePositive = (balanceCents ?? 0) >= 0;

  return (
    <section
      className="mb-6 rounded-xl border border-hairline bg-[rgb(var(--surface-raised-rgb)/0.6)] p-4 md:p-5"
      aria-label={t('cockpit.title')}
    >
      {/* Header + period selector */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-accent-ink flex items-center gap-2">
          <IconChartPie size={14} className="text-accent-ink" />
          {t('cockpit.title')}
        </h2>

        <div className="flex items-center gap-2">
          <span className="text-xs text-faint mr-1">{t('cockpit.period.label')}</span>
          {/* Granularity toggle */}
          <div className="flex rounded-lg border border-hairline overflow-hidden">
            {(['month', 'quarter', 'year'] as SummaryPeriodKind[]).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => handleKindChange(k)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  kind === k
                    ? 'bg-indigo-600 text-white'
                    : 'bg-surface-raised text-muted hover:text-primary'
                }`}
              >
                {t(`cockpit.period.${k}`)}
              </button>
            ))}
          </div>
          {/* Concrete period select */}
          <select
            value={period.fromISO}
            onChange={(e) => handlePeriodChange(e.target.value)}
            aria-label={t('cockpit.period.label')}
            className="rounded-lg border border-hairline bg-surface-raised px-3 py-1.5 text-xs text-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {periodOptions.map((p) => (
              <option key={p.fromISO} value={p.fromISO}>
                {periodLabel(p)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Key-figure tiles — all four share h-full + flex flex-col so the grid's
          default stretch alignment renders them at equal height (D4: the
          balance tile no longer carries the multi-line disclaimer, which used
          to make it ~twice as tall as its siblings). */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* 1 — Ausgaben (netto) */}
        <div className="rounded-lg bg-[rgb(var(--surface-base-rgb)/0.6)] border border-hairline p-4 h-full flex flex-col">
          <p className="text-xs text-muted">{t('cockpit.expenseNet')}</p>
          <p className="mt-1 text-xl font-bold text-primary font-mono tabular-nums">
            {formatCents(summary.expenseNetCents)}
          </p>
        </div>

        {/* 2 — Abziehbare Vorsteuer */}
        <div className="rounded-lg bg-[rgb(var(--surface-base-rgb)/0.6)] border border-hairline p-4 h-full flex flex-col">
          <p className="text-xs text-muted">
            {t('cockpit.vatDeductible')}{' '}
            <span className="text-[var(--tone-success-fg)]">({t('cockpit.vatDeductibleHint')})</span>
          </p>
          <p className="mt-1 text-xl font-bold text-[var(--tone-success-fg)] font-mono tabular-nums">
            {formatCents(summary.vatDeductibleCents)}
          </p>
        </div>

        {/* 3 — Geschuldete USt (§13b) + A1 draft hint (stays IN this tile — short,
            state-dependent; height parity comes from h-full on the siblings). */}
        <div className="rounded-lg bg-[rgb(var(--surface-base-rgb)/0.6)] border border-hairline p-4 h-full flex flex-col">
          <p className="text-xs text-muted">{t('cockpit.vatOwed')}</p>
          <p className="mt-1 text-xl font-bold text-[var(--tone-warning-fg)] font-mono tabular-nums">
            {formatCents(summary.vatOwedCents)}
          </p>
          {summary.excludedDraftVatOwedCount > 0 && (
            <p className="mt-2 flex items-start gap-1.5 text-xs text-[var(--tone-warning-fg)]">
              <IconWarning size={12} className="mt-0.5 flex-shrink-0" />
              <span>
                {t('cockpit.vatOwedDraftHint', { count: summary.excludedDraftVatOwedCount })}
              </span>
            </p>
          )}
        </div>

        {/* 4 — Einnahmen − Ausgaben (brutto) — sub-flag gated via income prop.
            Disclaimer + basis note moved to the footer strip below the grid
            (D4) — this tile now shows only title + value, same shape as 1–3. */}
        {showBalanceTile && (
          <div className="rounded-lg bg-[rgb(var(--surface-base-rgb)/0.6)] border border-hairline p-4 h-full flex flex-col">
            <p className="text-xs text-muted">{t('cockpit.balanceTitle')}</p>
            {incomeLoading && income === null ? (
              <p className="mt-2 flex items-center gap-2 text-sm text-faint">
                <IconSpinner className="animate-spin" />
                {t('list.loading')}
              </p>
            ) : balanceCents === null ? (
              <p className="mt-1 text-sm text-faint">{t('cockpit.incomeUnavailable')}</p>
            ) : (
              <p
                className={`mt-1 text-xl font-bold font-mono tabular-nums ${
                  balancePositive ? 'text-[var(--tone-success-fg)]' : 'text-[var(--tone-danger-fg)]'
                }`}
              >
                {formatSignedCents(balanceCents)}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Footer — A2 Pflicht-Disclaimer + Basis-Hinweis (sichtbar gerendert,
          NICHT tooltip-only; jurist-gebunden, Wortlaut unverändert), nur wenn
          die Saldo-Kachel überhaupt gezeigt wird, plus der immer sichtbare
          "nur gebuchte Ausgaben"-Hinweis. Volle Breite unter dem Grid statt
          in einer einzelnen Kachel — bläht keine Kachel mehr auf (D4). */}
      <div className="mt-3 space-y-1">
        {showBalanceTile && (
          <>
            <p className="text-[11px] leading-snug text-faint">
              {t('cockpit.balanceDisclaimer')}
            </p>
            <p className="text-[11px] leading-snug text-faint">
              {t('cockpit.balanceBasisNote')}
            </p>
          </>
        )}
        <p className="text-[11px] text-gray-600">{t('cockpit.includedHint')}</p>
      </div>
    </section>
  );
};

export default ExpenseSummaryBar;
